// Mirrors the relay.contacts schema (see
// supabase/migrations/20260417000000_create_relay_schema_contacts.sql
// + 20260601130000_relay_unified_contact_company_stage.sql)
// + the relay.contact_social_profiles join.

import { STAGES, type Stage } from "@/lib/stages";
import type { LeadType } from "@/lib/lead-type";

// Contacts and companies share one unified lifecycle "stage" set
// (see @/lib/stages and the relay.contacts/companies status CHECK constraints).
export const CONTACT_STATUSES = STAGES;
export type ContactStatus = Stage;

export const CONTACT_SOURCES = [
  "manual",
  "import",
  "booking",
  "social",
  "api",
  "document",
] as const;
// Free text since 20260601150000 — a built-in token above or an org-defined
// custom source from relay.lead_sources.
export type ContactSource = string;

export const SOCIAL_PLATFORMS = [
  "instagram",
  "facebook",
  "linkedin",
  "x",
  "tiktok",
] as const;
export type SocialPlatform = (typeof SOCIAL_PLATFORMS)[number];

export interface ContactSocialProfile {
  id: string;
  platform: SocialPlatform;
  username: string | null;
  profile_url: string | null;
  verified: boolean;
}

export interface Contact {
  id: string;
  organization_id: string;

  first_name: string | null;
  last_name: string | null;
  email: string | null;
  phone: string | null;
  mobile: string | null;
  company: string | null;
  company_id: string | null;
  job_title: string | null;
  website: string | null;
  avatar_url: string | null;

  status: ContactStatus;
  // B2B (person at a company) vs B2C (individual is the customer).
  lead_type: LeadType;
  source: ContactSource;
  source_detail: string | null;
  owner_id: string | null;

  // Social reach (e.g. follower count) — helps gauge UGC collaboration potential.
  follower_count: number | null;

  // Imported contact input/history; separate from computed ContactScore.score.
  lead_score?: number | null;

  ai_summary: string | null;
  ai_summary_updated_at: string | null;

  address_line1: string | null;
  address_line2: string | null;
  address_city: string | null;
  address_state: string | null;
  address_postal_code: string | null;
  address_country: string | null;

  notes: string | null;
  metadata: Record<string, unknown>;

  // Email-suppression flag (CAN-SPAM / unsubscribe). true = never email this
  // contact. Written by the CSV importer and unsubscribe handling; exposed as
  // a list filter. Non-nullable boolean in relay.contacts (defaults false).
  do_not_email: boolean;

  created_at: string;
  updated_at: string;
  deleted_at: string | null;

  social_profiles?: ContactSocialProfile[];
}

/** Subset of Contact shape that's writable from a form. */
export type ContactInput = Partial<
  Pick<
    Contact,
    | "first_name"
    | "last_name"
    | "email"
    | "phone"
    | "mobile"
    | "company"
    | "company_id"
    | "job_title"
    | "website"
    | "status"
    | "lead_type"
    | "source"
    | "source_detail"
    | "follower_count"
    | "address_line1"
    | "address_line2"
    | "address_city"
    | "address_state"
    | "address_postal_code"
    | "address_country"
    | "notes"
    | "owner_id"
  >
>;

export interface ContactImportRecord {
  id: string;
  organization_id: string;
  imported_by: string | null;
  file_name: string;
  total_rows: number;
  imported_count: number;
  skipped_count: number;
  error_count: number;
  status: "pending" | "processing" | "completed" | "failed";
  error_details: Record<string, unknown>;
  created_at: string;
  updated_at: string;
}

export interface ContactScore {
  id: string;
  organization_id: string;
  contact_id: string;
  score: number;
  score_breakdown: {
    activities?: number;
    email_opens?: number;
    email_clicks?: number;
    deals?: number;
    sequences?: number;
    form_submissions?: number;
    social?: number;
    [key: string]: number | undefined;
  };
  last_scored_at: string;
}

export function contactDisplayName(c: Pick<Contact, "first_name" | "last_name" | "email">): string {
  const name = [c.first_name, c.last_name].filter(Boolean).join(" ").trim();
  return name || c.email || "Unnamed";
}
